import React, { useContext } from "react";
import styles from "./styles.module.css";
import SyntaxHighlighter from '../../../components/SyntaxHighlighter'
import { data } from "./data";
import { lineStyle } from "../../../constants";
import { AppContext } from "../../../AppContext";

const PopulationPyramid = (props) => {
  const { isMobile } = props;
  const appContext = useContext(AppContext);
  const { theme } = appContext;
  return (
    <div
      className={styles.container}
      style={{ paddingLeft: isMobile ? 3 : 20 }}
    >
      <div style={{maxWidth: 1100, marginRight: isMobile ? 0 : 130}}>
      <h2>
        Population Pyramid Charts
        <a
          style={{ marginLeft: 20, fontSize: 15, color: theme==='dark'? 'skyblue' : 'blue' }}
          target="_blank"
          href="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/PopulationPyramid/PopulationPyramid.md"
          rel="noreferrer"
        >
          props table
        </a>
      </h2>
      <div style={{ marginLeft: 10, marginBottom: 100 }}>
        <h4>Minimal Population Pyramid Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Population pyramid charts are generally used to show number of male
            and female individuals of different age groups. It may also
            highlight the surplus (extra) males or females in each age group.
            <br />
            <br />
            Although the name is Population Pyramid, you are free to use this
            chart to showcase any similar use case. The only condition is that
            each object in the data array has 2 parts-{" "}
            <b>
              <i>left</i>
            </b>{" "}
            and{" "}
            <b>
              <i>right</i>
            </b>
          </p>
          <p>A simple Population pyramid chart can be made with just-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[0].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[0].imgSrc} className={styles.pyrOutput} alt="" />
          <p>
            Just add the prop <b>showValuesAsBarLabels</b> to show values as
            labels beside the left and right bars.
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[1].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[1].imgSrc} className={styles.pyrOutput} alt="" />
          <p>
            Alternatively, you can manually provide the left and right bar
            labels by adding the <b>leftBarLabel</b> and <b>rightBarLabel</b>{" "}
            properties to the data objects.
            <br />
            Here's an example-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[2].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[2].imgSrc} className={styles.pyrOutput} alt="" />
          <p>
            Notice that we are free to provide the labels to whichever bars we
            like, and leave others empty.
          </p>
        </div>

        <hr />

        <div style={{ marginLeft: 10 }}>
          <h4>midAxis</h4>
          <p>
            We can create a gap between the left and right bars, and can
            optionally add labels there. We call this <b>midAxis</b>. <br />
            Just add the prop <b>showMidAxis</b> to render a midAxis in the
            chart.
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[3].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[3].imgSrc} className={styles.pyrOutput} alt="" />
          <p>
            Add the property <b>midAxisLabel</b> to the data objects to show a
            label in the midAxis between the left and right bars.
          </p>
          <p>Some other properties of the midAxis are-</p>
          <ul>
            <li>
              <b>midAxisThickness</b>
            </li>
            <li>
              <b>midAxisLabelWidth</b>
            </li>
            <li>
              <b>midAxisLeftColor</b>
            </li>
            <li>
              <b>midAxisRightColor</b>
            </li>
            <li>
              <b>midAxisStrokeDashArray</b>
            </li>
            <li>
              <b>midAxisLabelColor</b>
            </li>
            <li>
              <b>midAxisLabelFontStyle</b>
            </li>
            <li>
              <b>midAxisLabelFontWeight</b>
            </li>
            <li>
              <b>midAxisLabelFontFamily</b>
            </li>
          </ul>
          <p>
            Note that most of these properties can be passed both as a prop in
            the <b>PopulationPyramid</b> component or as a property in the data
            objects.
          </p>
        </div>

        <div style={{ marginLeft: 10 }}>
          <h4>yAxisLabel</h4>
          <p>
            You might have noticed there's some unnecessary space on the left
            side of each chart shown above.
            <br />
            That space is left to show y-axis labels there. You can add the
            property <b>yAxisLabel</b> to the data objects, or you can get rid
            of that unnecessary space using the prop <b>hideYAxisText</b>.
          </p>
        </div>

        <hr />

        <div style={{ marginLeft: 10 }}>
          <h4>Understanding the Pyramid</h4>
          <p>
            Some useful properties of a population pyramid chart are shown in
            the labelled image below-
          </p>
          <img
            src={require("../../../assets/popnPyramidLabelled.png")}
            alt="population pyramid labelled"
            className={isMobile ? styles.pyrLabelledMobile : styles.pyrLabelled}
          />
        </div>
        <div style={{ marginLeft: 10 }}>
          <h4>showSurplus</h4>
          <p>
            Did you notice the dark blue and the dark red colored highlights in
            the left and right bars of the above chart? They are actually the
            surplus or difference between the left and right values.
            <br />
            To show the surplus in the bars, use the prop <b>showSurplus</b>.
            The colors of the surplus can be changed using the{" "}
            <b>leftSurplusColor</b> and the <b>rightSurplusColor</b> props.
          </p>
          <p>Other props related to surplus are-</p>
          <ul>
            <li>
              <b>showSurplusLeft</b>
            </li>
            <li>
              <b>showSurplusRight</b>
            </li>
            <li>
              <b>leftSurplusBorderColor</b>
            </li>
            <li>
              <b>rightSurplusBorderColor</b>
            </li>
            <li>
              <b>leftSurplusBorderWidth</b>
            </li>
            <li>
              <b>rightSurplusBorderWidth</b>
            </li>
          </ul>
          <p>
            A complete description of all the props can be found here-{" "}
            <a
              style={{color: theme==='dark'? 'skyblue' : 'blue'}}
              href="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/PopulationPyramid/PopulationPyramid.md"
              target="_blank"
              rel="noreferrer"
            >
              Population Pyramid props
            </a>
          </p>
          <p>
            <span className={styles.noteText}>Note: </span>Most of the
            properties are present in both data objects as well as separate
            props. The property is first searched in the data objects. If not
            found, it is searched in the props. If not found found in props, the
            default value is applied, as listed in the props table (in the link
            shared above).
          </p>
        </div>
        <hr />
      </div>
      </div>
    </div>
  );
};

export default PopulationPyramid;
